import { PlusButton } from "@components/buttons/plus";
import {
  SHBox,
  SHButton,
  SHHtmlBlock,
  SHStack,
  SHTableCell,
  SHTableCellProps,
  SHTypography,
} from "@components/design-systems";
import { EditSVG } from "@components/svgs";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { AnalysisSummaryDTO } from "@models/platform-analysis/entities/steps/summary";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { useTheme } from "@mui/material";
import { AnalysisDynamicStepsOrder } from "@pages/platform-analysis-v2/_id/constants";
import { ChangeRichTextDialog } from "@pages/platform-analysis/_id/steps/summary/components/change-rich-text-dialog";
import { hexToRGBA } from "@utils";
import { isEmpty } from "lodash";
import { memo, useMemo } from "react";
import { Controller, FieldPath, useFormContext } from "react-hook-form";
import { useToggle } from "react-use";
import { AnalysisSummaryStaticValueConfigs as Configs } from "../../../configs";

interface SummaryCommentCellProps extends SHTableCellProps {
  fieldPath: FieldPath<AnalysisSummaryDTO>;
  commentSettingFieldPath: FieldPath<AnalysisSummaryDTO>;
  analysisId: string;
  productId: string;
  step: AnalysisStep;
  isDisabled?: boolean;
  isViewMode?: boolean;
  isAppendix?: boolean;
  removedAtStep?: AnalysisStep;
}

export const SummaryCommentCell = memo(
  ({
    fieldPath,
    commentSettingFieldPath,
    analysisId,
    productId,
    step,
    isDisabled = false,
    isViewMode = false,
    isAppendix = false,
    removedAtStep,
    ...others
  }: SummaryCommentCellProps) => {
    //#region Hooks & State
    const { palette } = useTheme();
    const { updateDecision } = usePlatformAnalysis();
    const { control, watch } = useFormContext<AnalysisSummaryDTO>();
    const [openCommentDialog, toggleOpenCommentDialog] = useToggle(false);
    const [isSubmitting, toggleIsSubmitting] = useToggle(false);
    const commentSetting = watch(commentSettingFieldPath);

    const disableComment = useMemo(
      () => isDisabled || !commentSetting,
      [isDisabled, commentSetting],
    );

    const showRemovedLabel = useMemo(
      () => removedAtStep === step,
      [removedAtStep, step],
    );

    const showCommentButton = useMemo(
      () =>
        !isAppendix ||
        (step &&
          removedAtStep &&
          AnalysisDynamicStepsOrder.indexOf(step) <=
            AnalysisDynamicStepsOrder.indexOf(removedAtStep)) ||
        step === AnalysisStep.Summary,
      [isAppendix, step, removedAtStep],
    );

    //#region Methods
    const RemovedLabel = () => (
      <SHBox
        sx={{
          p: "4px 15px",
          border: `1px solid ${hexToRGBA(palette.error[300], 0.1)}`,
          borderRadius: "80px",
          backgroundColor: hexToRGBA(palette.error[200], 0.1),
        }}
      >
        <SHTypography
          variant="subtitle1"
          fontSize="13px"
          color={palette.error.main}
        >
          Removed from shortlist
        </SHTypography>
      </SHBox>
    );

    const renderEmptyComment = () => (
      <SHStack
        justifyContent="center"
        alignItems="center"
        gap="7px"
        sx={{ height: "100%" }}
      >
        {!isViewMode && showCommentButton && (
          <PlusButton
            disabled={disableComment}
            size="medium"
            onClick={toggleOpenCommentDialog}
          >
            Comment
          </PlusButton>
        )}

        {!isViewMode && !showCommentButton && (
          <SHTypography variant="body1">-</SHTypography>
        )}

        {isViewMode && showCommentButton && (
          <SHTypography colorVariant="third" variant="body2">
            No comment
          </SHTypography>
        )}

        {isViewMode && !showCommentButton && (
          <SHTypography variant="body1">-</SHTypography>
        )}

        {showRemovedLabel && <RemovedLabel />}
      </SHStack>
    );

    const renderComment = (comment: string) => (
      <SHStack
        alignItems="flex-start"
        justifyContent="space-between"
        spacing="7px"
        sx={{ height: "100%" }}
      >
        <SHHtmlBlock
          content={comment}
          variant="body4"
          color={palette.text.third}
        />
        {showRemovedLabel && <RemovedLabel />}
        {!isViewMode && (
          <SHButton
            startIcon={<EditSVG color={palette.text.primary} />}
            variant="outlined"
            disabled={disableComment}
            sx={{
              "&:active svg>path": { fill: palette.common.white },
              "&:disabled svg>path": { fill: palette.text.disabled },
            }}
            onClick={toggleOpenCommentDialog}
          >
            Edit
          </SHButton>
        )}
      </SHStack>
    );

    const setComment = async (comment: string | null) => {
      return await updateDecision({
        analysisId: analysisId,
        analysisStep: step,
        productId: productId,
        decisionStatus: null,
        comment: comment,
      });
    };

    const handleOnUpdateComment = async (value: string | null) => {
      toggleIsSubmitting(true);
      setTimeout(async () => {
        await setComment(value);
        toggleIsSubmitting(false);
        toggleOpenCommentDialog(false);
      }, 0);
    };

    //#region Render
    return (
      <Controller
        name={fieldPath}
        control={control}
        render={({ field: { value, onChange } }) => (
          <SHTableCell
            sx={{
              height: "auto",
              borderLeft: `solid 1px ${hexToRGBA(palette.primary[50], 0.3)}`,
              minWidth: "250px",
            }}
            {...others}
          >
            {isEmpty(value) ? renderEmptyComment() : renderComment(value)}
            {openCommentDialog && (
              <ChangeRichTextDialog
                onSubmit={(value) => {
                  onChange(value);
                  handleOnUpdateComment(value);
                }}
                title={isEmpty(value) ? "Add comment" : "Edit comment"}
                placeHolder={"Enter your comment"}
                initialContent={value ?? null}
                isSubmitting={isSubmitting}
                onClose={() => {
                  toggleOpenCommentDialog();
                }}
                maxLength={Configs.MaximumCharacterComment}
                isCommentBox
              />
            )}
          </SHTableCell>
        )}
      />
    );
  },
);
